import { repaint } from 'utils/node';

class PredictiveSearch {
  private input: HTMLInputElement;

  private predictiveSearchResults: HTMLElement;

  private emptyState: HTMLElement;

  private form: HTMLFormElement;

  private requestData: {
    endpoint: string;
    resources: Array<string>;
    limit: number;
    sectionId: string;
    options: Array<string>;
  };

  constructor(element: HTMLElement, input: HTMLInputElement) {
    this.input = input;
    this.predictiveSearchResults = element.querySelector('#predictive-search');
    this.emptyState = document.querySelector('#predictive-search-empty');

    this.form = element.querySelector('form[role="search"]');

    this.requestData = {
      endpoint: window.theme?.endpoints?.predictiveSearch || '/search/suggest',
      resources: ['product', 'article'],
      limit: 4,
      sectionId: 'predictive-search',
      options: ['title', 'product_type', 'vendor', 'tag', 'variants.sku', 'variants.title'],
    };

    this.input.addEventListener('input', PredictiveSearch.debounce(() => {
      this.onChange();
    }, 300).bind(this));

    if (this.input.value !== '') this.onChange();
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm: string) {
    let fetchURL = `${this.requestData.endpoint}?q=${searchTerm}&resources[limit]=${this.requestData.limit}&section_id=${this.requestData.sectionId}&resources[options][fields]=${this.requestData.options}`;

    if (this.requestData.resources.length) {
      fetchURL += `&resources[type]=${this.requestData.resources.join(',')}`;
    }

    fetch(fetchURL)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status.toString());
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        document.dispatchEvent(new Event('app:remount'));
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  open() {
    this.predictiveSearchResults.hidden = false;
    repaint(this.predictiveSearchResults);

    this.emptyState.hidden = true;
  }

  close() {
    this.predictiveSearchResults.hidden = true;

    this.emptyState.hidden = false;
    repaint(this.emptyState);
  }

  static debounce<F extends (...params: any[]) => void>(fn: F, delay: number) {
    let timeoutID: number = null;
    // eslint-disable-next-line func-names
    return function (this: any, ...args: any[]) {
      clearTimeout(timeoutID);
      timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
    } as F;
  }
}

export default (
  element: HTMLElement,
  input: HTMLInputElement,
): PredictiveSearch => new PredictiveSearch(element, input);
